/**
 * Two people, two markers: who has locked in and who has not.
 *
 * The shape Hunsy keeps needing. A Sync round reveals nothing until both have locked in; a Pulse
 * check-in shows a direction only once both have rated; a co-op challenge does not start until
 * both have opted in. All three are the same fact — a pair of booleans — and all three were being
 * drawn as a sentence ("Waiting for Gabi to opt in"), which costs a line of prose per occurrence
 * and reads as a nudge aimed at the person who has not moved.
 *
 * Two rules come out of that and both are load-bearing:
 *
 *   YOURS IS ALWAYS FIRST.  The pair is drawn from the viewer's seat, so `me` decides the order
 *                           rather than 'A' before 'B'. A marker pair that swapped sides depending
 *                           on which half of the pairing you are would be unreadable, and the two
 *                           partners would be looking at mirror-image screens describing one fact.
 *   IT STATES, IT DOES NOT ASK.  No press, no callback, no "remind them". This is a status glyph.
 *                           A control here would turn "they have not answered yet" into a receipt,
 *                           which is the pressure this app's whole notification design refuses to
 *                           create.
 *
 * Colour is the partner's identity colour, never a semantic one — see `@/lib/partner`. Waiting is
 * deliberately NOT drawn in `--warning`: nobody is late, and a partner is never the warning one.
 */
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { otherPartner, partnerTint, type PartnerId, type PartnerTint } from '@/lib/partner';
import * as React from 'react';
import { View } from 'react-native';

/**
 * `sm` rides inside a row of text; `md` stands alone as the answer to "are we both in yet".
 * Numbers rather than classes because the diameter, the radius and the ring all have to agree.
 */
const DIAMETER = { sm: 16, md: 22 } as const;

/**
 * The tick, sized as a static class per step so NativeWind can extract it. It carries no colour:
 * the ink is the partner's, and arrives as an inline style.
 */
const TICK_CLASS = { sm: 'text-[10px] leading-[12px]', md: 'text-[13px] leading-[15px]' } as const;

const RING_WIDTH = 2;

export function LockPair({
  me,
  mine,
  theirs,
  mineLabel,
  theirsLabel,
  size = 'md',
  className,
}: {
  me: PartnerId;
  mine: boolean;
  theirs: boolean;
  mineLabel?: string;
  theirsLabel?: string;
  size?: 'sm' | 'md';
  className?: string;
}): React.JSX.Element {
  const diameter = DIAMETER[size];
  const tickClass = TICK_CLASS[size];

  /**
   * Labels are all-or-nothing so the two markers stay on one baseline: given one, the other falls
   * back to its default rather than collapsing its column and dropping its marker half a line.
   */
  const labelled = mineLabel !== undefined || theirsLabel !== undefined;
  const mineName = mineLabel ?? 'you';
  const theirsName = theirsLabel ?? 'them';

  /**
   * One label for the pair, not two.
   *
   * `accessibilityRole="image"` makes this a single node whose children are not announced —
   * otherwise a screen reader reads two unlabelled circles, which is exactly as useful as it
   * sounds. The wording is the sentence this component replaced.
   */
  const spoken = `${mineName}: ${mine ? 'locked in' : 'waiting'} · ${theirsName}: ${theirs ? 'locked in' : 'waiting'}`;

  return (
    <View
      accessible
      accessibilityRole="image"
      accessibilityLabel={spoken}
      /* LABELLED PAIRS SIT FURTHER APART, because the labels are wider than the rings and it is
         the LABELS that collide. Two 22px markers 6px apart are unmistakably two markers; "YOU"
         and "GABI" 6px apart under them, tracked-out and uppercase, read as one word — which is
         what the readiness pass caught on the co-op card. 12px is the smallest gap at which the
         two name columns separate at every width the app draws them at. */
      className={cn('flex-row items-start', labelled ? 'gap-3' : 'gap-1.5', className)}>
      <Marker
        tint={partnerTint(me)}
        locked={mine}
        diameter={diameter}
        tickClass={tickClass}
        label={labelled ? mineName : undefined}
      />
      <Marker
        tint={partnerTint(otherPartner(me))}
        locked={theirs}
        diameter={diameter}
        tickClass={tickClass}
        label={labelled ? theirsName : undefined}
      />
    </View>
  );
}

/**
 * Filled with a tick, or an empty ring in the same colour.
 *
 * Both states carry the partner's colour — the ring is not a grey placeholder — because the colour
 * says WHO and the fill says WHETHER. Greying out the waiting one would make two facts share one
 * channel, and the pair would stop reading as two people.
 *
 * AND BOTH STATES CARRY THE RING. The edge is the gel's `line`, not its `fill`: B is midnight,
 * which is Tuxedo dark's card colour and near enough Bouquet dark's, so a filled B marker drawn
 * with no edge is a hole in the card — the disc, the tick and all. The sheets set it once for both
 * states (`.lock-ring { border: 2px solid var(--p-edge) }`, and `.lock-filled` re-states the
 * colour rather than removing the border), and `line` is the fill again wherever the fill already
 * reads, so the ring costs nothing on the markers that never needed it.
 */
function Marker({
  tint,
  locked,
  diameter,
  tickClass,
  label,
}: {
  tint: PartnerTint;
  locked: boolean;
  diameter: number;
  tickClass: string;
  label?: string;
}) {
  return (
    <View className="items-center gap-1">
      <View
        className="items-center justify-center"
        style={{
          width: diameter,
          height: diameter,
          borderRadius: diameter / 2,
          backgroundColor: locked ? tint.fill : 'transparent',
          borderWidth: RING_WIDTH,
          borderColor: tint.line,
        }}>
        {locked ? (
          <Text className={cn(tickClass, 'font-semibold')} style={{ color: tint.ink }}>
            ✓
          </Text>
        ) : null}
      </View>
      {label ? (
        /* `label`, which IS `caption` plus the muted colour these were always written with — the
           variant the restyle added for exactly this: the boards' tracked uppercase name under a
           mark. The ring above it keeps its circle; see the radius rules. */
        <Text variant="label">{label}</Text>
      ) : null}
    </View>
  );
}
